import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Alert } from 'antd';
import {
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getLeadScores,
  deleteLeadScore,
  scoreAllEntities,
  SCORING_ENTITY_TYPES,
  LEAD_GRADES,
  type LeadScore,
  type LeadScoreFilters,
} from '@/api/analyticsApi';
import { useAuth } from '@/hooks/useAuth';
import AnalyticsNav from './AnalyticsNav';

const GRADE_COLORS: Record<string, string> = {
  A: 'green',
  B: 'cyan',
  C: 'gold',
  D: 'orange',
  F: 'red',
};

export default function LeadScoreListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [entityTypeFilter, setEntityTypeFilter] = useState<string | undefined>();
  const [gradeFilter, setGradeFilter] = useState<string | undefined>();
  const [scoringAll, setScoringAll] = useState(false);

  const canRead = hasPermission('analytics', 'read');
  const canWrite = hasPermission('analytics', 'write');
  const canDelete = hasPermission('analytics', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: LeadScoreFilters = { ...params };
      if (entityTypeFilter) filters.entity_type = entityTypeFilter;
      if (gradeFilter) filters.grade = gradeFilter;
      return getLeadScores(filters);
    },
    [entityTypeFilter, gradeFilter]
  );

  const handleScoreAll = useCallback(
    (entityType: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('lead_scoring.confirm_score_all', { entity_type: t(`lead_scoring.entity_type.${entityType}`) }),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        onOk: async () => {
          setScoringAll(true);
          try {
            const result = await scoreAllEntities(entityType);
            message.success(
              t('lead_scoring.score_all_success', { count: result.entities_scored })
            );
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          } finally {
            setScoringAll(false);
          }
        },
      });
    },
    [t]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('lead_scoring.confirm_delete_score'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteLeadScore(id);
            message.success(t('lead_scoring.score_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<LeadScore> = useMemo(
    () => [
      {
        title: t('lead_scoring.field.entity_type'),
        dataIndex: 'entity_type',
        key: 'entity_type',
        width: 120,
        render: (v: string) => (
          <Tag color="blue">{t(`lead_scoring.entity_type.${v}`, v)}</Tag>
        ),
      },
      {
        title: t('lead_scoring.field.entity_id'),
        dataIndex: 'entity_id',
        key: 'entity_id',
        width: 220,
        ellipsis: true,
      },
      {
        title: t('lead_scoring.field.total_score'),
        dataIndex: 'total_score',
        key: 'total_score',
        width: 100,
        sorter: true,
      },
      {
        title: t('lead_scoring.field.grade'),
        dataIndex: 'grade',
        key: 'grade',
        width: 80,
        render: (v: string) => (
          <Tag color={GRADE_COLORS[v] ?? 'default'}>{v}</Tag>
        ),
      },
      {
        title: t('lead_scoring.field.rules_matched'),
        dataIndex: 'rules_matched',
        key: 'rules_matched',
        width: 120,
        render: (matched: number, record: LeadScore) =>
          `${matched} / ${record.rules_evaluated}`,
      },
      {
        title: t('lead_scoring.field.scored_at'),
        dataIndex: 'scored_at',
        key: 'scored_at',
        width: 160,
        sorter: true,
        render: (v: string) =>
          v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('app.actions'),
        key: 'col_actions',
        width: 80,
        render: (_: unknown, record: LeadScore) => (
          <Space size="small">
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, canDelete, handleDelete]
  );

  const toolbar = (
    <Space>
      <Select
        allowClear
        placeholder={t('lead_scoring.field.entity_type')}
        value={entityTypeFilter}
        onChange={(v) => { setEntityTypeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 160 }}
        options={SCORING_ENTITY_TYPES.map((et) => ({
          value: et,
          label: t(`lead_scoring.entity_type.${et}`),
        }))}
      />
      <Select
        allowClear
        placeholder={t('lead_scoring.field.grade')}
        value={gradeFilter}
        onChange={(v) => { setGradeFilter(v); gridRef.current?.reload(); }}
        style={{ width: 100 }}
        options={LEAD_GRADES.map((g) => ({
          value: g,
          label: g,
        }))}
      />
      {canWrite && (
        <Select
          placeholder={t('lead_scoring.score_all')}
          style={{ width: 180 }}
          loading={scoringAll}
          value={undefined}
          onSelect={(v) => {
            if (v) void handleScoreAll(v);
          }}
          options={SCORING_ENTITY_TYPES.map((et) => ({
            value: et,
            label: `${t('lead_scoring.score_all')}: ${t(`lead_scoring.entity_type.${et}`)}`,
          }))}
        />
      )}
    </Space>
  );

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <AnalyticsNav />
      <h2>{t('lead_scoring.scores_title')}</h2>
      <DataGrid<LeadScore>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('lead_scoring.search_scores_placeholder')}
        defaultSortField="total_score"
        defaultSortDescending
        toolbar={toolbar}
      />
    </div>
  );
}
